import { API_BASE_URL, apiGet, apiGetOptional, apiPost } from '@/api/http';

import type { Message } from '@/types/interview';

export type UnreadMessagesSummary = {
  unread_message_cnt: number;
  client_companies?: Array<{
    id: string;
    unread_message_cnt: number;
    interviews: Array<{ id: string; unread_message_cnt: number }>;
  }>;
  recruiters?: Array<{
    id: string;
    unread_message_cnt: number;
    interviews: Array<{ id: string; unread_message_cnt: number }>;
  }>;
};

export const getSupportMessageCounter = () =>
  apiGet<{ unread_message_cnt: number }>(
    `${API_BASE_URL}conversations/users/admin/unread-messages/count/`,
  );

export const getUnreadMessagesCount = () =>
  apiGet<UnreadMessagesSummary>(
    `${API_BASE_URL}conversations/unread-messages/count/`,
  );

export const markAllMessagesAsRead = () =>
  apiPost(`${API_BASE_URL}conversations/mark-all-as-read/`);

export const getAdminContacts = (query = '') =>
  apiGet(`${API_BASE_URL}conversations/users/${query}`);

export const getAllConversations = (query = '') =>
  apiGet(`${API_BASE_URL}conversations/all/${query}`);

export const getUserRoom = (userId: string, query = '') =>
  apiGetOptional(
    `${API_BASE_URL}conversations/users/${userId}/messages/${query}`,
  );

export const getSupportRoom = (query = '') =>
  apiGet(`${API_BASE_URL}conversations/users/admin/messages/${query}`);

export const getInterviewRoom = (interviewId: string, query = '') =>
  apiGetOptional(
    `${API_BASE_URL}conversations/interviews/${interviewId}/messages/${query}`,
  );

export const getDirectConversation = (
  recruiterUserOrClientCompanyId: string,
  query = '',
) =>
  apiGetOptional(
    `${API_BASE_URL}conversations/direct/${recruiterUserOrClientCompanyId}/messages/${query}`,
  );

export const sendUserMessage = (
  userId: string,
  text: string,
  file?: File | Blob,
) => {
  if (file) {
    const formData = new FormData();
    formData.append('text', text);
    formData.append('file', file);
    return apiPost<Message>(
      `${API_BASE_URL}conversations/users/${userId}/messages/`,
      formData,
    );
  }
  return apiPost<Message>(
    `${API_BASE_URL}conversations/users/${userId}/messages/`,
    {
      text,
    },
  );
};

export const sendSupportMessage = (text: string, file?: File | Blob) => {
  if (file) {
    const formData = new FormData();
    formData.append('text', text);
    formData.append('file', file);
    return apiPost<Message>(
      `${API_BASE_URL}conversations/users/admin/messages/`,
      formData,
    );
  }
  return apiPost<Message>(
    `${API_BASE_URL}conversations/users/admin/messages/`,
    {
      text,
    },
  );
};

export const sendInterviewMessage = (
  interviewId: string,
  text: string,
  file?: File | Blob,
) => {
  if (file) {
    const formData = new FormData();
    formData.append('text', text);
    formData.append('file', file);
    return apiPost<Message>(
      `${API_BASE_URL}conversations/interviews/${interviewId}/messages/`,
      formData,
    );
  }
  return apiPost<Message>(
    `${API_BASE_URL}conversations/interviews/${interviewId}/messages/`,
    { text },
  );
};

export const sendDirectMessage = (
  otherParty: string,
  text: string,
  file?: File | Blob,
) => {
  if (file) {
    const formData = new FormData();
    formData.append('text', text);
    formData.append('file', file);
    return apiPost<Message>(
      `${API_BASE_URL}conversations/direct/${otherParty}/messages/`,
      formData,
    );
  }
  return apiPost<Message>(
    `${API_BASE_URL}conversations/direct/${otherParty}/messages/`,
    { text },
  );
};

export const submitStageScreeningAnswers = (
  interviewId: string,
  answers: Record<string, unknown>,
  stage: string,
) =>
  apiPost(
    `${API_BASE_URL}interviews/${interviewId}/stage-screening-questions/answer/`,
    { answers, stage },
  );
